import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, firestore } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  Badge,
  History,
  LockReset,
  Edit
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#013a64',
    },
    secondary: {
      main: '#4CAF50',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 600,
    },
  },
});

const ProfileScreen = () => {
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    studentId: '',
    profilePicture: '',
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          console.log("No user signed in");
          setLoading(false);
          return;
        }

        const docRef = doc(firestore, 'students', user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setUserData({
            name: `${docSnap.data().FirstName} ${docSnap.data().Surname}`,
            email: docSnap.data().EmailAddress,
            phoneNumber: docSnap.data().PhoneNumber,
            studentId: docSnap.data().StudentID,
            profilePicture: docSnap.data().ProfilePicture || '',
          });
        } else {
          console.log("No such student data!");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!auth.currentUser) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h6" color="error">
          You need to be logged in to view this page
        </Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 3 }}
          onClick={() => navigate('/login')}
        >
          Go to Login
        </Button>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Header */}
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Back
            </Typography>
            <IconButton color="inherit" onClick={() => navigate('#')}>
              <Edit />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="sm" sx={{ py: 3, flex: 1 }}>
          <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {/* Profile Header */}
            <Box sx={{ 
              backgroundColor: 'primary.main', 
              p: 3, 
              textAlign: 'center',
              color: 'white'
            }}>
              {userData.profilePicture ? (
                <Avatar 
                  src={userData.profilePicture} 
                  alt="Profile" 
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    margin: 'auto',
                    border: '3px solid white',
                    boxShadow: 3
                  }} 
                />
              ) : (
                <Avatar 
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    margin: 'auto',
                    bgcolor: 'white',
                    color: 'primary.main',
                    border: '3px solid white',
                    boxShadow: 3
                  }}
                >
                  <Person sx={{ fontSize: 50 }} />
                </Avatar>
              )}
              <Typography variant="h5" sx={{ mt: 2, fontWeight: 'bold' }}>
                {userData.name}
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                {userData.studentId}
              </Typography>
            </Box>

            {/* Profile Details */}
            <List sx={{ p: 0 }}>
              <ListItem>
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  <Email />
                </ListItemIcon>
                <ListItemText 
                  primary="Email" 
                  secondary={userData.email || 'Not provided'} 
                  secondaryTypographyProps={{ color: 'text.primary' }}
                />
              </ListItem>
              <Divider variant="inset" component="li" />

              <ListItem>
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  <Phone />
                </ListItemIcon>
                <ListItemText 
                  primary="Phone Number" 
                  secondary={userData.phoneNumber || 'Not provided'} 
                  secondaryTypographyProps={{ color: 'text.primary' }}
                />
              </ListItem>
              <Divider variant="inset" component="li" />

              <ListItem>
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  <Badge />
                </ListItemIcon>
                <ListItemText 
                  primary="Student ID" 
                  secondary={userData.studentId || 'Not provided'} 
                  secondaryTypographyProps={{ color: 'text.primary' }}
                />
              </ListItem>
            </List>

            {/* Action Buttons */}
            <CardContent sx={{ pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<History />}
                sx={{ mb: 2, py: 1.5, borderRadius: 1 }}
                onClick={() => navigate('/RequestHistory')}
              >
                View Request History
              </Button>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<LockReset />}
                sx={{ py: 1.5, borderRadius: 1 }}
                onClick={() => navigate('/ChangePassword')}
              >
                Change Password
              </Button>
            </CardContent>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ProfileScreen;